/** State of a single task as it moves through the queue. */
export type BatchItemStatus = 'in-progress' | 'done' | 'error';

/**
 * Fired as each queued task changes state, keyed by the item's index in the
 * array passed to `run`, so the caller can drive a per-row status.
 */
export type BatchProgressCallback = (
  index: number,
  status: BatchItemStatus
) => void;

export interface BatchRunResult {
  /** Number of tasks that resolved. */
  succeeded: number;
  /** Number of tasks that threw. */
  failed: number;
}

/**
 * Runs an async task over a list of items one at a time.
 *
 * The naive approach — `items.forEach((i) => task(i))` — fires every task at
 * once, which gives no ordering guarantee and can flood a server when the list
 * is long. This drains the queue strictly sequentially: each task completes
 * (and reports its status) before the next one starts. A single failure is
 * reported per-item and does not stop the queue.
 *
 * @typeParam T - the type of each item being processed.
 *
 * @example
 * const runner = new SequentialBatchRunner<Req>(
 *   (req) => client.doSomething(req),
 *   (error) => showErrorDialog('client.doSomething', error)
 * );
 * const unsubscribe = runner.subscribe((i, status) => updateRow(i, status));
 * const { succeeded, failed } = await runner.run(requests);
 * unsubscribe();
 */
export class SequentialBatchRunner<T> {
  private onProgress: BatchProgressCallback | null = null;
  private cancelled = false;

  constructor(
    /** The async unit of work to run for each item, in order. */
    private task: (item: T, index: number) => Promise<unknown>,
    /** Invoked with the raw error whenever a single task throws. */
    private onError?: (error: unknown, item: T, index: number) => void
  ) {}

  subscribe(callback: BatchProgressCallback): () => void {
    this.onProgress = callback;
    return () => {
      if (this.onProgress === callback) {
        this.onProgress = null;
      }
    };
  }

  /** Stops the queue; the in-flight task still settles, later ones are skipped. */
  cancel(): void {
    this.cancelled = true;
  }

  /**
   * Runs the task for each item in order, awaiting one before starting the
   * next. Resolves once the queue is drained (or cancelled) with a
   * success/failure tally.
   */
  async run(items: T[]): Promise<BatchRunResult> {
    this.cancelled = false;
    let succeeded = 0;
    let failed = 0;

    for (let index = 0; index < items.length; index++) {
      if (this.cancelled) {
        break;
      }

      this.onProgress?.(index, 'in-progress');
      try {
        await this.task(items[index], index);
        succeeded++;
        this.onProgress?.(index, 'done');
      } catch (error) {
        failed++;
        this.onProgress?.(index, 'error');
        this.onError?.(error, items[index], index);
      }
    }

    return { succeeded, failed };
  }
}
